import { digger } from '@cbre/digger';
import { formatNumber } from '@miq/utils';
import moment from 'moment';
import { LocalizationOptions } from '..';
import { UTC_EDP_INPUT_FORMAT } from '../../shared/constants/date.const';
import {
  formatDate,
  getUserRatePrefs,
  isNotNullOrEmptyString,
  isTenantEntityActive,
  processRateOpts,
  sanitizeValueOr,
  symbolRetriever,
} from '../../shared/utils/functions';
import {
  SettingsLeaseType,
  SpaceCardDetails,
} from '../../stacking-plan-container/actions-toolbar/settings/settings.model';
import {
  EDPLeaseCompInfo,
  EDPSpaceInfo,
  EDPTenantInfo,
  SuiteType,
  TenantRateCols,
  UserSettingsPreference,
} from '../../stacking-plan-container/floor-container/floor/item/item.model';
import { MappedLov } from '../../stacking-plan.model';
import { CompanyColloquial } from '../colloquials/company-colloquial.class';
import { DatesRange, DatesRangeFlag, RecordTimestamp } from '../dates.models';
import { RepresentativeClass } from '../reps/representative.class';
import { AbstractSpaceClass, SpaceClassOptions } from './abstract-space.class';
import { AvailabilitySpaceClass } from './availability-space.class';
import { shouldDisableByConfidentialityLevels } from '@miq/domain';

const PROPERTY_TYPE_RETAIL = 'Retail';

export class TenantSpaceClass extends AbstractSpaceClass {
  public availabilitySpace: AvailabilitySpaceClass;

  public tenant: EDPTenantInfo;
  public lease: EDPLeaseCompInfo;

  // tenant
  public tenantColloquial: CompanyColloquial[];
  public estimatedRentAmount: number;
  public estimatedRentAmountUom: string;

  // lease
  public tenantReps: RepresentativeClass[];
  public tenantInfo: RepresentativeClass[];
  public isUserLessThanRecordConf: boolean;

  get companyName(): string {
    const localizationOptions = this.options.localization || ({} as LocalizationOptions);
    const { isTurnedOn = false, country, language } = localizationOptions;
    let resultCompanyName = this.tenant?.party_organization_display_name;
    if (isTurnedOn) {
      const queriedColloquial = CompanyColloquial.findByCountryAndLanguage(this.tenantColloquial, country, language);
      resultCompanyName =
        queriedColloquial != null && isNotNullOrEmptyString(queriedColloquial.alternativeCompanyName)
          ? queriedColloquial.alternativeCompanyName
          : resultCompanyName;
    }
    return resultCompanyName;
  }

  get tenantId(): number {
    return this.tenant?.id;
  }

  get leaseId(): number {
    return this.lease?.id;
  }

  get spaceId(): number {
    return this.tenant?.space_id;
  }

  get isEditableByUser(): boolean {
    return isTenantEntityActive() ? true : shouldDisableByConfidentialityLevels(this.lease as any);
  }

  constructor(edpSpaceInfo: EDPSpaceInfo, options?: Partial<SpaceClassOptions>) {
    const isTenantEntity = isTenantEntityActive();

    super(edpSpaceInfo, isTenantEntity ? SuiteType.Tenant : SuiteType.Lease, options);
    const { tenant, lease, availability } = edpSpaceInfo;
    this.isUserLessThanRecordConf = this.getUserLessThanRecordConf(lease);
    if (isTenantEntity) {
      this.initTenantValues(tenant);
    } else {
      this.initTenantFromLeaseValues(lease);
    }

    this.initLeaseValues(lease);
    this.availabilitySpace = availability != null ? new AvailabilitySpaceClass(edpSpaceInfo, options) : undefined;
  }

  public getAreaSpace(): number {
    return this.tenant.leased_space_size;
  }

  public getAreaSpaceUOM(): string {
    return this.tenant.leased_space_size_uom_desc;
  }

  public getBrandName(): string {
    const { brand_id, brand_name } = this.tenant;
    return brand_id != null && isNotNullOrEmptyString(brand_name) ? brand_name : this.lease?.brand_name;
  }

  public getBrandId(): number {
    const { brand_id, brand_name } = this.tenant;
    return brand_id != null && isNotNullOrEmptyString(brand_name) ? brand_id : this.lease?.brand_id;
  }

  /**
   * Review if leasing rent rate & UOM exists and return the concatenated value.
   * Return only the leasing rent rate if UOM is not present, else returns null.
   */
  public generateRentRate: () => string;

  public generateItemRentRate(userPref: UserSettingsPreference, settingSelection?: SettingsLeaseType): string {
    const isMonthly = userPref === UserSettingsPreference.Monthly;
    const monthlyYearlyAbrev = isMonthly ? 'Mo' : 'Yr';
    const currencySymbol = symbolRetriever('US Dollar');
    if (!settingSelection) {
      return null;
    }
    const settingCol = this.getRentRateCols(settingSelection);
    const displayedRent = isMonthly ? settingCol.monthly : settingCol.yearly;
    return `${currencySymbol}${displayedRent} ${monthlyYearlyAbrev}`;
  }

  getRentRateCols(settings: SettingsLeaseType): TenantRateCols {
    const baseRentCols = {
      monthly: formatNumber(this.lease?.base_rent_monthly, undefined, '.2-2') ?? '-',
      yearly: formatNumber(this.lease?.base_rent_yearly, undefined, '.2-2') ?? '-',
    };
    const askingRentCols = {
      monthly: formatNumber(this.lease?.asking_rent_monthly, undefined, '.2-2') ?? '-',
      yearly: formatNumber(this.lease?.asking_rent_yearly, undefined, '.2-2') ?? '-',
    };
    const effRentCols = {
      monthly: formatNumber(this.lease?.effective_rent_monthly, undefined, '.2-2') ?? '-',
      yearly: formatNumber(this.lease?.effective_rent_yearly, undefined, '.2-2') ?? '-',
    };

    switch (settings) {
      case SettingsLeaseType.AskingRent:
        return askingRentCols;
      case SettingsLeaseType.BaseRent:
        return baseRentCols;
      case SettingsLeaseType.EffectiveRent:
        return effRentCols;
      default:
    }
  }

  public generateRentRateValue(): number {
    return this.estimatedRentAmount;
  }

  public generateRentRateUOM(): string {
    return this.estimatedRentAmountUom;
  }

  /**
   * Return space dates depending on certain scenarios
   *
   * - Lease data has precedence to tenant data
   * - If no lease dates, use tenant dates —without mixing dates between records.
   * - If lease dates are incomplete but tenant dates are complete use tenant dates
   *
   * @param recordToUse
   */
  public getSpaceDates(): DatesRange {
    const { tenancy_start_date, tenancy_end_date, occupancy_date } = this.tenant;
    return { startDate: tenancy_start_date, endDate: tenancy_end_date, occupancyDate: occupancy_date };
  }

  public getSpaceDatesEstF(): DatesRangeFlag {
    const { tenancy_start_date_estF, tenancy_end_date_estF, estimated_rent_amount_estF, leased_space_size_estF } =
      this.tenant;
    return {
      startDateFlag: tenancy_start_date_estF,
      endDateFlag: tenancy_end_date_estF,
      rentRateFlag: estimated_rent_amount_estF,
      spaceSizeFlag: leased_space_size_estF,
    };
  }

  public getRecordTimestamp(): RecordTimestamp {
    const { edp_last_update_date: leaseEDPUpdateDate, edp_last_update_by: leaseEDPUpdateBy } = this.lease || {};
    const { edp_last_update_date: tenantEDPUpdateDate, edp_last_update_by: tenantEDPUpdateBy } = this.tenant;
    let result = { lastUpdatedDate: leaseEDPUpdateDate, lastUpdatedBy: leaseEDPUpdateBy };
    const areLeaseTSEmpty = leaseEDPUpdateDate == null && leaseEDPUpdateBy == null;
    const isLeaseComplete = leaseEDPUpdateDate != null && leaseEDPUpdateBy != null;
    const isTenantComplete = tenantEDPUpdateDate != null && tenantEDPUpdateBy != null;

    if (areLeaseTSEmpty || (!isLeaseComplete && isTenantComplete)) {
      result = { lastUpdatedDate: leaseEDPUpdateDate, lastUpdatedBy: tenantEDPUpdateBy };
    }
    return result;
  }

  public generateTenantRepAgency(): string {
    const repAgency = (this.tenantReps || [])
      .map(val => val.companyName)
      .filter(isNotNullOrEmptyString)
      .join(', ');
    return isNotNullOrEmptyString(repAgency) ? repAgency : null;
  }
  public generateTenantInfo(): string {
    const companyName = (this.tenantInfo || [])
      .map(val => val.companyName)
      .filter(isNotNullOrEmptyString)
      .join(', ');
    return isNotNullOrEmptyString(companyName) ? companyName : null;
  }

  public getTenantStatus(): string {
    return this.tenant.tenancy_status;
  }

  public getPropertyType(): string {
    return this.tenant.ref_property_usage_type_desc;
  }

  public getNotes(): string {
    return this.tenant.tenancy_notes;
  }

  public isCurrentSpace(): boolean {
    const { startDate, endDate } = this.getSpaceDates();
    const now = moment();
    const startDateMoment = moment(startDate, UTC_EDP_INPUT_FORMAT);
    const endDateMoment = moment(endDate, UTC_EDP_INPUT_FORMAT);
    if (!startDateMoment.isValid() || !endDateMoment.isValid()) {
      return false;
    }
    const isStartDateBeforeOrToday = startDateMoment.isSameOrBefore(now);
    const isEndDateAfterToday = endDateMoment.isAfter(now);
    return startDate != null && endDate != null && isStartDateBeforeOrToday && isEndDateAfterToday;
  }

  public isFutureSpace(): boolean {
    const { startDate } = this.getSpaceDates();
    return startDate != null && moment(startDate, UTC_EDP_INPUT_FORMAT).isAfter(moment());
  }

  public toString(options: { detailsToDisplay: SpaceCardDetails; mappedUomLov?: MappedLov }): string {
    let title = sanitizeValueOr(AbstractSpaceClass.unknown, this.companyName);
    const brandName = this.getBrandName();
    if (this.tenant.ref_property_usage_type_desc === PROPERTY_TYPE_RETAIL && brandName != null) {
      title = brandName;
    }
    const { mappedUomLov } = options;
    const cleansedOptionsToDesplay = options.detailsToDisplay;
    cleansedOptionsToDesplay['leaseRateOpts'] = processRateOpts(cleansedOptionsToDesplay['leaseRateOpts']);
    const fieldsToDisplay = AbstractSpaceClass.getRequiredFieldsToDisplay(cleansedOptionsToDesplay);
    const monthlyOrYearlyPreference = getUserRatePrefs();
    const fieldsData = {
      suiteNumber: sanitizeValueOr(AbstractSpaceClass.unknown, this.getSuiteNumber(this.options)),
      spaceSize: sanitizeValueOr(AbstractSpaceClass.unknown, this.generateFloorSpace(mappedUomLov || null)),
      leaseEndingDate: formatDate(
        sanitizeValueOr(AbstractSpaceClass.unknown, this.getSpaceDates().endDate) as string,
        'MM/YYYY'
      ),
      // There is no rentRate in Lease or Tenancy. leaseRateOpts is controlled by table setting on the stacking plan.
      // Rentrate will be checked to display leaseRateOpts while downloading.
      rentDate: sanitizeValueOr(
        AbstractSpaceClass.unknown,
        this.generateItemRentRate(
          monthlyOrYearlyPreference,
          cleansedOptionsToDesplay.leaseRateOpts as SettingsLeaseType
        )
      ),
      tenantRepAgency: sanitizeValueOr(AbstractSpaceClass.unknown, this.generateTenantRepAgency()),
      tenantInfo: sanitizeValueOr(AbstractSpaceClass.unknown, this.generateTenantInfo()),
    };

    return fieldsToDisplay.reduce(
      (acc, fieldKey) => (fieldsData[fieldKey] ? `${acc}\n ${fieldsData[fieldKey]}` : acc),
      `${title}`
    );
  }

  public internalSpaceInfoClone(): Pick<EDPSpaceInfo, 'tenant' | 'lease' | 'availability' | 'non_rentable_space'> {
    const colloquial = Array.isArray(this.tenantColloquial)
      ? this.tenantColloquial.map(tenantColloquial => tenantColloquial.toEDPColloquial())
      : [];
    const tenant = { ...this.tenant, colloquial };
    const lease = this.buildEDPLeaseInfo();
    return { tenant, lease, availability: null, non_rentable_space: null };
  }

  public enableEditEvent(): boolean {
    return true;
  }

  private buildEDPLeaseInfo(): EDPLeaseCompInfo {
    if (this.lease?.id == null) {
      return null;
    }
    const tenantReps = Array.isArray(this.tenantReps)
      ? this.tenantReps.map(tenantRep => tenantRep.toEDPRepresentativeInfo())
      : null;

    const tenantInfo = Array.isArray(this.tenantInfo)
      ? this.tenantInfo.map(tenantInfoInstance => tenantInfoInstance.toEDPRepresentativeInfo())
      : [];

    const lease: EDPLeaseCompInfo = { ...this.lease, tenant_reps: tenantReps, tenant_info: tenantInfo };
    return Object.values(lease).some(val => val != null) ? lease : null;
  }

  #getCompanyFieldValue = (tenantInfo: Record<string, any>, fieldName: string) => {
    const val = digger(tenantInfo, fieldName);
    if (val == null || typeof val === 'string') {
      return val ?? '';
    }
    return Array.isArray(val) ? val?.join?.(',') : val;
  };

  private initTenantFromLeaseValues(edpLeaseCompInfo: EDPLeaseCompInfo) {
    if (edpLeaseCompInfo) {
      const { tenant_info: tenantInfo } = edpLeaseCompInfo;
      const colloquial = digger(tenantInfo, '/colloquial');
      const companyId = digger(tenantInfo, '/company_id');
      const companyName = this.#getCompanyFieldValue(tenantInfo, '/party_organization_display_name');
      const companyLegalName = this.#getCompanyFieldValue(tenantInfo, '/company_legal_name');
      const companyTradeName = this.#getCompanyFieldValue(tenantInfo, '/company_trade_name');
      const ultimateParentCompany = this.#getCompanyFieldValue(tenantInfo, '/ultimate_parent_company_name');

      this.tenant = {
        id: edpLeaseCompInfo.id,
        access_control: edpLeaseCompInfo.access_control,
        space_id: null,
        confidentiality_f: edpLeaseCompInfo.confidentiality_f,
        company_id: companyId,
        company_name: companyName,
        party_organization_display_name: companyName,
        company_legal_name: companyLegalName,
        company_trade_name: companyTradeName,
        edp_last_update_date: edpLeaseCompInfo.edp_last_update_date,
        edp_last_update_by: edpLeaseCompInfo.edp_last_update_by,
        tenancy_notes: edpLeaseCompInfo.lease_arrangement_notes,
        ref_cbre_industry_code_desc: edpLeaseCompInfo.ref_cbre_industry_code_desc,
        ref_industry_classification_desc: edpLeaseCompInfo.ref_industry_classification_desc,
        ref_property_usage_type_desc: edpLeaseCompInfo.ref_property_usage_type_desc,
        ref_property_usage_sub_type_desc: edpLeaseCompInfo.ref_property_usage_sub_type_desc,
        leased_space_size: edpLeaseCompInfo.arrangement_space_area,
        leased_space_size_uom_desc: edpLeaseCompInfo.ref_arrangement_space_area_uom_desc,
        active_f: edpLeaseCompInfo.active_f,
        miq_record_hide_f: edpLeaseCompInfo.miq_record_hide_f,
        brand_id: edpLeaseCompInfo.brand_id,
        brand_name: edpLeaseCompInfo.brand_name,
        business_category_desc: edpLeaseCompInfo.business_category_desc,
        estimated_rent_amount: edpLeaseCompInfo.base_rent_monthly,
        estimated_rent_amount_uom: edpLeaseCompInfo.ref_base_rent_monthly_uom_desc,
        estimated_rent_amount_estF: edpLeaseCompInfo.base_rent_monthly_estimated_f,
        leased_space_size_estF: edpLeaseCompInfo.total_leased_space_estimated_f,
        external_opportunity_number: edpLeaseCompInfo.external_opportunity_number,
        external_opportunity_number_source_desc: edpLeaseCompInfo.external_opportunity_number_source_desc,
        full_floor_f: edpLeaseCompInfo.full_floor_f,
        sublease_f: edpLeaseCompInfo.sublease_f,
        tenancy_start_date_estF: edpLeaseCompInfo.lease_start_date_estimated_f,
        tenancy_end_date_estF: edpLeaseCompInfo.lease_end_date_estimated_f,
        colloquial,
        tenancy_base_rent_amount_monthly: edpLeaseCompInfo.base_rent_monthly,
        tenancy_base_rent_amount_monthly_uom: edpLeaseCompInfo.ref_base_rent_monthly_uom_desc,
        space_condition_type_desc: edpLeaseCompInfo.space_condition_type_desc,
        ultimate_parent_company_name: ultimateParentCompany,
        layout_cbre_f: edpLeaseCompInfo.layout_cbre_f,
        tenancy_start_date: edpLeaseCompInfo.space_tenancy_start_date,
        tenancy_end_date: edpLeaseCompInfo.space_tenancy_end_date,
        occupancy_date: edpLeaseCompInfo.space_occupancy_date,
        tenancy_status: edpLeaseCompInfo.space_tenancy_status_desc,
      };
      this.setEstimatedRentAmount();
      this.tenantColloquial = CompanyColloquial.fromEDPColloquialArray(this.tenant.colloquial);
    }
  }

  private initTenantValues(edpTenantInfo: EDPTenantInfo): void {
    this.tenant = edpTenantInfo;
    this.setEstimatedRentAmount();
    this.tenantColloquial = CompanyColloquial.fromEDPColloquialArray(this.tenant.colloquial);
  }

  private initLeaseValues(edpLeaseCompInfo: EDPLeaseCompInfo): void {
    if (edpLeaseCompInfo != null) {
      this.lease = edpLeaseCompInfo;
      this.tenantReps = RepresentativeClass.fromEDPRepresentativeInfoArray(edpLeaseCompInfo.tenant_reps, this.options);
      this.tenantInfo = RepresentativeClass.fromEDPRepresentativeInfoArray(edpLeaseCompInfo.tenant_info);
    }
  }

  private setEstimatedRentAmount(): void {
    this.estimatedRentAmount = this.tenant.estimated_rent_amount;
    this.estimatedRentAmountUom = this.tenant.estimated_rent_amount_uom;
  }

  private getUserLessThanRecordConf(lease: EDPLeaseCompInfo): boolean {
    const isNumber = value => value !== '' && value != null && !isNaN(Number(value));
    const leaseConfidentiality = lease?.confidentiality_f;
    const userConfidentiality = lease?.miqProperties?.userConfidentiality;
    if (
      leaseConfidentiality &&
      isNumber(leaseConfidentiality) &&
      userConfidentiality &&
      isNumber(userConfidentiality)
    ) {
      return Number(userConfidentiality) < Number(leaseConfidentiality);
    }
    return false;
  }
}
